import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for managing chat container height based on input container height
 * @param {boolean} iselements - Whether elements mode is active
 * @param {boolean} isSurvey - Whether survey mode is active
 * @param {string} transcript - Current transcript text
 * @param {string} chatChange - Current chat input value
 * @returns {Object} Height state and input container ref
 */
export default function useChatHeight(iselements, isSurvey, transcript, chatChange) {
  const [inputHeight, setInputHeight] = useState(0);
  const [chatMaxHeight, setChatMaxHeight] = useState("88%");
  const inputContainerRef = useRef(null);

  // Effect to observe input height changes
  useEffect(() => {
    if (iselements || !inputContainerRef.current) return;

    const observeInputHeight = () => {
      const inputContainer = inputContainerRef.current;
      if (inputContainer) {
        const height = inputContainer.offsetHeight;
        setInputHeight(height);

        // Calculate new max height for chat container
        // Use different base height depending on screen height
        const screenHeight = window.innerHeight;
        const baseHeight = screenHeight < 890 ? 88 : 94;
        const newMaxHeight = `calc(${baseHeight}% - ${height}px)`;
        setChatMaxHeight(newMaxHeight);
      }
    };

    // Initial measurement
    observeInputHeight();

    // Use ResizeObserver to watch for height changes
    const resizeObserver = new ResizeObserver(() => {
      observeInputHeight();
    });

    resizeObserver.observe(inputContainerRef.current);

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [isSurvey, transcript, chatChange, iselements]);

  return {
    inputHeight,
    chatMaxHeight,
    inputContainerRef
  };
}
